import { type ReactNode, useMemo } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import {
  ThemeProvider as MuiThemeProvider,
  alpha,
  createTheme,
  darken,
  getContrastRatio,
  lighten,
} from '@mui/material/styles';

import { useBrandingStore } from '@/stores/useBrandingStore';
import { useDirectionStore } from '@/stores/useDirectionStore';
import { useThemeStore } from '@/stores/useThemeStore';

import { darkTheme, lightTheme } from './theme';

interface AppThemeProviderProps {
  children: ReactNode;
}

/**
 * Picks white or black as contrast text for a given background color.
 * Uses WCAG AA threshold (4.5:1).
 */
function pickContrastText(bg: string): string {
  return getContrastRatio(bg, '#ffffff') >= 4.5 ? '#ffffff' : '#000000';
}

/**
 * Builds gradient CSS strings from a primary color.
 * light → main → dark for full icon gradient.
 * very-light → light for soft badge/chip backgrounds.
 */
function buildIconGradients(primary: string) {
  const light = lighten(primary, 0.35);
  const dark = darken(primary, 0.25);
  const veryLight = lighten(primary, 0.55);
  return {
    icon: `linear-gradient(135deg, ${light} 0%, ${primary} 50%, ${dark} 100%)`,
    iconSoft: `linear-gradient(135deg, ${veryLight} 0%, ${light} 100%)`,
  };
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const mode = useThemeStore((state) => state.mode);
  const direction = useDirectionStore((s) => s.direction);
  const mainColor = useBrandingStore((s) => s.mainColor);
  const secondaryColor = useBrandingStore((s) => s.secondaryColor);
  const bgColor = useBrandingStore((s) => s.bgColor);

  const theme = useMemo(() => {
    const baseTheme = mode === 'dark' ? darkTheme : lightTheme;

    // Always apply direction; branding overrides only layer on top
    const overrides: Record<string, unknown> = { direction };

    const hasBranding = mainColor ?? secondaryColor ?? bgColor;
    if (!hasBranding) return createTheme(baseTheme, overrides);

    const paletteOverrides: Record<string, unknown> = {};

    if (mainColor) {
      const primaryDark = darken(mainColor, 0.2);
      const primaryLight = lighten(mainColor, 0.3);
      const contrastText = pickContrastText(mainColor);

      paletteOverrides.primary = {
        main: mainColor,
        dark: primaryDark,
        light: primaryLight,
        contrastText,
      };

      // Surface tokens derived from primary — keep neutral tokens from the base palette
      paletteOverrides.surface = {
        ...baseTheme.palette.surface,
        // Option card: very subtle primary tint on selection
        optionSelectedBg: alpha(mainColor, 0.08),
        // Active radio ring / close-panel button text — darkened primary
        radioActive: darken(mainColor, 0.3),
        // Execute-code button text — deeply darkened for readability
        executeText: darken(mainColor, 0.5),
        // Dropzone border — light primary tint
        dropzoneBorder: lighten(mainColor, 0.25),
        // Browse file button border — slightly lighter
        browseButtonBorder: lighten(mainColor, 0.35),
        // Code action border — neutral-primary blend
        codeActionBorder: alpha(primaryDark, 0.3),
        // Icon color: use primary.main (the admin's color itself) — softer than the derived dark
        iconColor: mainColor,
      };

      // Icon gradient strings stored in the palette so any component can access them
      // via theme.palette.gradients.icon / theme.palette.gradients.iconSoft
      paletteOverrides.gradients = buildIconGradients(mainColor);
    }

    if (secondaryColor) {
      paletteOverrides.secondary = {
        main: secondaryColor,
        dark: darken(secondaryColor, 0.2),
        light: lighten(secondaryColor, 0.3),
        contrastText: pickContrastText(secondaryColor),
      };
    }

    if (bgColor) {
      // Only override the page canvas; cards stay white / base paper color
      paletteOverrides.background = {
        ...baseTheme.palette.background,
        default: bgColor,
      };
    }

    overrides.palette = paletteOverrides;
    return createTheme(baseTheme, overrides);
  }, [mode, direction, mainColor, secondaryColor, bgColor]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
